/*
  hanger-look.js — enhancement layer for the Hanger-style skin.
  Original implementation for The Ark. Vanilla JS, no dependencies, ~4 KB.
  Handles: featured-tabs switching + carousel arrows, category carousel
  arrows, video hero autoplay/pause + ready fade, PDP buy-row grouping,
  and skeleton→loaded marking for media. Everything is idempotent (per-
  element guards) so it is safe in the Shopify theme editor and safe if
  the script is evaluated more than once.
*/
(function () {
  'use strict';

  /* ==== VERSION MARKER — open your browser Console (F12) on the live site.
     If you do NOT see this line, the OLD hanger-look.js is still live and the
     fullscreen arrows/dots CANNOT work until you replace assets/hanger-look.js. */
  if (window.console && console.log) console.log('%c[The Ark] hanger-look.js ARK-JS-REV-35 loaded', 'color:#0a0;font-weight:bold');

  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function scrollStep(track) {
    var item = track.querySelector(':scope > *');
    var gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 16;
    return item ? item.getBoundingClientRect().width + gap : track.clientWidth * 0.8;
  }

  function smoothScrollBy(track, left) {
    track.scrollBy({ left: left, behavior: REDUCED ? 'auto' : 'smooth' });
  }

  function updateArrowState(track, prev, next) {
    if (!track || !prev || !next) return;
    var max = track.scrollWidth - track.clientWidth - 2;
    prev.disabled = track.scrollLeft <= 2;
    next.disabled = track.scrollLeft >= max;
  }

  /* ------- Featured tabs ------- */
  function initFeaturedTabs(root) {
    if (root.dataset.hftReady) return;
    root.dataset.hftReady = 'true';

    var tabs = root.querySelectorAll('[data-hft-tab]');
    var panels = root.querySelectorAll('[data-hft-panel]');
    var prev = root.querySelector('[data-hft-prev]');
    var next = root.querySelector('[data-hft-next]');

    function activeTrack() {
      var panel = root.querySelector('[data-hft-panel].is-active');
      return panel ? panel.querySelector('[data-hft-track]') : null;
    }

    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        if (tab.classList.contains('is-active')) return;
        var idx = tab.getAttribute('data-hft-tab');
        tabs.forEach(function (t) {
          var on = t === tab;
          t.classList.toggle('is-active', on);
          t.setAttribute('aria-selected', on ? 'true' : 'false');
        });
        panels.forEach(function (p) {
          var on = p.getAttribute('data-hft-panel') === idx;
          p.classList.toggle('is-active', on);
          if (on) {
            p.removeAttribute('hidden');
          } else {
            p.setAttribute('hidden', '');
          }
        });
        var track = activeTrack();
        if (track) {
          track.scrollLeft = 0;
          updateArrowState(track, prev, next);
        }
      });
    });

    if (prev && next) {
      prev.addEventListener('click', function () {
        var track = activeTrack();
        if (track) smoothScrollBy(track, -scrollStep(track));
      });
      next.addEventListener('click', function () {
        var track = activeTrack();
        if (track) smoothScrollBy(track, scrollStep(track));
      });
      panels.forEach(function (p) {
        var track = p.querySelector('[data-hft-track]');
        if (track) {
          track.addEventListener('scroll', function () {
            if (p.classList.contains('is-active')) updateArrowState(track, prev, next);
          }, { passive: true });
        }
      });
      var t = activeTrack();
      if (t) updateArrowState(t, prev, next);
      window.addEventListener('resize', function () {
        var track = activeTrack();
        if (track) updateArrowState(track, prev, next);
      });
    }
  }

  /* ------- Category carousel ------- */
  function initCategoryCarousel(root) {
    if (root.dataset.hccReady) return;
    root.dataset.hccReady = 'true';

    var track = root.querySelector('[data-hcc-track]');
    var prev = root.querySelector('[data-hcc-prev]');
    var next = root.querySelector('[data-hcc-next]');
    if (!track) return;

    if (prev && next) {
      prev.addEventListener('click', function () { smoothScrollBy(track, -scrollStep(track)); });
      next.addEventListener('click', function () { smoothScrollBy(track, scrollStep(track)); });
      track.addEventListener('scroll', function () { updateArrowState(track, prev, next); }, { passive: true });
      updateArrowState(track, prev, next);
    }
  }

  /* ------- Video hero: autoplay assist, ready fade, pause toggle ------- */
  function initVideoHero(root) {
    if (root.dataset.hvhReady) return;
    root.dataset.hvhReady = 'true';

    /* two videos may exist (desktop + mobile variant) — drive the VISIBLE one
       and silence the hidden one so it doesn't buffer/play in the background */
    var videos = Array.prototype.slice.call(root.querySelectorAll('video'));
    var video = null;
    for (var vi = 0; vi < videos.length; vi++) {
      if (getComputedStyle(videos[vi]).display !== 'none') { video = videos[vi]; break; }
    }
    if (!video) video = videos[0] || null;
    videos.forEach(function (v) {
      if (v !== video) {
        try { v.autoplay = false; v.preload = 'none'; v.pause(); v.removeAttribute('autoplay'); } catch (e) {}
      }
    });
    if (video) {
      video.setAttribute('playsinline', '');
      video.muted = true;
      if (video.readyState >= 2) {
        root.classList.add('is-ready');
      } else {
        video.classList.add('h-video-wait');
        var ready = function () {
          video.classList.remove('h-video-wait');
          video.classList.add('h-video-in');
          root.classList.add('is-ready');
        };
        video.addEventListener('loadeddata', ready, { once: true });
        video.addEventListener('error', ready, { once: true });
      }
      var tryPlay = video.play();
      if (tryPlay && tryPlay.catch) tryPlay.catch(function () {});
    } else {
      /* image or placeholder — nothing to buffer */
      root.classList.add('is-ready');
    }

    var toggle = root.querySelector('[data-hvh-toggle]');
    if (!toggle || !video) return;
    toggle.addEventListener('click', function () {
      if (video.paused) {
        video.play();
        root.classList.remove('is-paused');
      } else {
        video.pause();
        root.classList.add('is-paused');
      }
    });
  }

  /* ------- PDP: group quantity + buy buttons onto one row ------- */
  function initPdpBuyRow(container) {
    if (container.dataset.hpdpReady) return;
    container.dataset.hpdpReady = 'true';

    var pf = container.querySelector('product-form.product-form');
    if (!pf) return;

    var buyWrap = pf;
    while (buyWrap.parentElement && buyWrap.parentElement !== container) {
      buyWrap = buyWrap.parentElement;
    }
    if (buyWrap.parentElement !== container) return;

    var qty = container.querySelector(':scope > .product-form__quantity, :scope > .product-form__input--quantity');
    var row = document.createElement('div');
    row.className = 'hpdp-buyrow';
    container.insertBefore(row, buyWrap);
    if (qty) row.appendChild(qty);
    buyWrap.classList.add('hpdp-buywrap');
    row.appendChild(buyWrap);
  }

  /* ------- PDP: interactive dots for the mobile media slider (replaces 1/N) ------- */
  function initGalleryDots(scope) {
    (scope || document).querySelectorAll('media-gallery .product__media-list.slider').forEach(function (track) {
      if (track.dataset.arkDots) return;
      var slides = Array.prototype.slice.call(track.querySelectorAll('.product__media-item'));
      if (slides.length < 2) return;
      var buttons = track.parentElement.querySelector('.slider-buttons');
      if (!buttons) return;
      track.dataset.arkDots = 'true';

      var dots = document.createElement('div');
      dots.className = 'hpdp-dots';
      slides.forEach(function (slide, i) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'hpdp-dot' + (i === 0 ? ' is-active' : '');
        b.setAttribute('aria-label', 'Go to image ' + (i + 1));
        b.addEventListener('click', function () {
          var tr = track.getBoundingClientRect();
          var sr = slide.getBoundingClientRect();
          track.scrollBy({ left: (sr.left + sr.width / 2) - (tr.left + tr.width / 2), behavior: 'smooth' });
        });
        dots.appendChild(b);
      });
      buttons.appendChild(dots);

      var raf = null;
      function sync() {
        raf = null;
        var tr = track.getBoundingClientRect();
        var center = tr.left + tr.width / 2;
        var active = 0, best = Infinity;
        slides.forEach(function (slide, i) {
          var sr = slide.getBoundingClientRect();
          var d = Math.abs((sr.left + sr.width / 2) - center);
          if (d < best) { best = d; active = i; }
        });
        var ds = dots.children;
        for (var i = 0; i < ds.length; i++) ds[i].classList.toggle('is-active', i === active);
      }
      track.addEventListener('scroll', function () {
        if (!raf) raf = requestAnimationFrame(sync);
      }, { passive: true });
    });
  }

  /* ------- Lightbox: prev/next arrows + dots for the fullscreen image slider ------- */
  function initLightboxDots(scope) {
    var tracks = (scope || document).querySelectorAll('.product-media-modal__content');
    if (window.console && console.log) console.log('[The Ark] lightbox: found ' + tracks.length + ' fullscreen modal(s)');
    tracks.forEach(function (track) {
      if (track.dataset.arkPxDots) return;
      var slides = Array.prototype.slice.call(track.children).filter(function (c) {
        if (!c || c.nodeType !== 1) return false;
        /* Dawn renders each fullscreen image as a DIRECT <img> child of the modal
           content — accept the media element ITSELF, not only wrappers around one. */
        if (c.matches && c.matches('img, video, iframe, model-viewer, deferred-media, product-model')) return true;
        return !!(c.querySelector && c.querySelector('img, video, iframe, model-viewer'));
      });
      if (window.console && console.log) console.log('[The Ark] lightbox: ' + slides.length + ' image(s) in this modal (need 2+ for arrows/dots)');
      if (slides.length < 2) return;
      track.dataset.arkPxDots = 'true';

      var dialog = track.closest('.product-media-modal__dialog') || track.parentElement;

      function current() {
        var tr = track.getBoundingClientRect();
        var center = tr.left + tr.width / 2;
        var active = 0, best = Infinity;
        slides.forEach(function (slide, i) {
          var sr = slide.getBoundingClientRect();
          var d = Math.abs((sr.left + sr.width / 2) - center);
          if (d < best) { best = d; active = i; }
        });
        return active;
      }
      function goTo(i) {
        i = Math.max(0, Math.min(slides.length - 1, i));
        var slide = slides[i];
        var tr = track.getBoundingClientRect();
        var sr = slide.getBoundingClientRect();
        track.scrollBy({ left: (sr.left + sr.width / 2) - (tr.left + tr.width / 2), behavior: REDUCED ? 'auto' : 'smooth' });
      }

      /* prev / next arrows */
      var prev = document.createElement('button');
      prev.type = 'button';
      prev.className = 'hpx-arrow hpx-arrow--prev';
      prev.setAttribute('aria-label', 'Previous image');
      prev.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 5l-7 7 7 7" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      prev.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); goTo(current() - 1); });

      var next = document.createElement('button');
      next.type = 'button';
      next.className = 'hpx-arrow hpx-arrow--next';
      next.setAttribute('aria-label', 'Next image');
      next.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 5l7 7-7 7" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      next.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); goTo(current() + 1); });

      /* dots */
      var dots = document.createElement('div');
      dots.className = 'hpx-dots';
      slides.forEach(function (slide, i) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'hpx-dot' + (i === 0 ? ' is-active' : '');
        b.setAttribute('aria-label', 'Image ' + (i + 1));
        b.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); goTo(i); });
        dots.appendChild(b);
      });

      dialog.appendChild(prev);
      dialog.appendChild(next);
      dialog.appendChild(dots);

      var raf = null;
      function sync() {
        raf = null;
        var active = current();
        var ds = dots.children;
        for (var i = 0; i < ds.length; i++) ds[i].classList.toggle('is-active', i === active);
        prev.classList.toggle('is-disabled', active <= 0);
        next.classList.toggle('is-disabled', active >= slides.length - 1);
      }
      track.addEventListener('scroll', function () {
        if (!raf) raf = requestAnimationFrame(sync);
      }, { passive: true });

      /* keyboard arrows while the lightbox is open */
      document.addEventListener('keydown', function (e) {
        var modalEl = track.closest('product-modal, .product-media-modal');
        if (!modalEl || !modalEl.hasAttribute('open')) return;
        if (e.key === 'ArrowLeft') { goTo(current() - 1); }
        else if (e.key === 'ArrowRight') { goTo(current() + 1); }
      });

      /* When the modal opens, land on the image that was clicked (Dawn's own
         scroll code targets its vertical layout and resets our slider), then
         keep the dots in sync. */
      function slideMediaId(el) {
        if (!el || !el.getAttribute) return null;
        var id = el.getAttribute('data-media-id');
        if (id) return id;
        var inner = el.querySelector && el.querySelector('[data-media-id]');
        return inner ? inner.getAttribute('data-media-id') : null;
      }
      var modal = track.closest('product-modal, .product-media-modal');
      if (modal && 'MutationObserver' in window) {
        new MutationObserver(function () {
          if (!modal.hasAttribute('open')) return;
          setTimeout(function () {
            var opener = modal.openedBy;
            if (opener && opener.getAttribute) {
              var mid = opener.getAttribute('data-media-id');
              if (mid) {
                for (var i = 0; i < slides.length; i++) {
                  if (slideMediaId(slides[i]) === String(mid)) { goTo(i); break; }
                }
              }
            }
            sync();
          }, 80);
          setTimeout(sync, 380);
        }).observe(modal, { attributes: true, attributeFilter: ['open'] });
      }
      sync();
    });
  }

  /* ------- Skeletons: mark media hosts loaded, fade late images ------- */
  var HOST_SELECTOR = '.hpc__media, .hcc__media, .card__media .media, .product__media.media, .slideshow .banner__media';

  function markLoaded(host) {
    if (!host.classList.contains('h-loaded')) host.classList.add('h-loaded');
  }

  function initMediaSkeleton(scope) {
    var hosts = (scope || document).querySelectorAll(HOST_SELECTOR);
    hosts.forEach(function (host) {
      if (host.dataset.hSkel) return;
      host.dataset.hSkel = 'true';
      var imgs = host.querySelectorAll('img');
      if (!imgs.length) {
        /* placeholder SVG or empty — no network wait */
        markLoaded(host);
        return;
      }
      var first = imgs[0];
      if (first.complete && first.naturalWidth > 0) {
        markLoaded(host);
      } else {
        var done = function () {
          if (!REDUCED) first.classList.add('h-img-in');
          markLoaded(host);
        };
        first.addEventListener('load', done, { once: true });
        first.addEventListener('error', function () { markLoaded(host); }, { once: true });
      }
    });
  }

  /* ------- PDP: floating add-to-cart bar (appears when buy box scrolls away) ------- */
  function initStickyAtc() {
    if (document.querySelector('.hatc')) return;
    var info = document.querySelector('.product__info-container');
    if (!info || !('IntersectionObserver' in window)) return;
    var submit = info.querySelector('.product-form__submit');
    var watch = info.querySelector('.hpdp-buyrow') || info.querySelector('product-form.product-form');
    if (!submit || !watch) return;

    var titleEl = document.querySelector('.product__title h1');
    var priceEl = info.querySelector('.price-item--regular, .price-item');
    var img = document.querySelector('.product__media img');

    var bar = document.createElement('div');
    bar.className = 'hatc';
    bar.setAttribute('aria-hidden', 'true');

    if (img) {
      var thumb = document.createElement('img');
      thumb.className = 'hatc__thumb';
      thumb.src = img.currentSrc || img.src;
      thumb.alt = '';
      thumb.loading = 'lazy';
      bar.appendChild(thumb);
    }
    var meta = document.createElement('div');
    meta.className = 'hatc__meta';
    var t = document.createElement('span');
    t.className = 'hatc__title';
    t.textContent = titleEl ? titleEl.textContent.trim() : '';
    var v = document.createElement('span');
    v.className = 'hatc__variant';
    var p = document.createElement('span');
    p.className = 'hatc__price';
    p.textContent = priceEl ? priceEl.textContent.trim() : '';
    meta.appendChild(t);
    meta.appendChild(v);
    meta.appendChild(p);
    bar.appendChild(meta);

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'hatc__btn';
    btn.textContent = submit.textContent.trim() || 'Add to cart';
    btn.addEventListener('click', function () {
      if (submit.getAttribute('disabled') === null && submit.getAttribute('aria-disabled') !== 'true') {
        submit.click();
      } else {
        info.scrollIntoView({ behavior: REDUCED ? 'auto' : 'smooth', block: 'center' });
      }
    });
    bar.appendChild(btn);
    document.body.appendChild(bar);

    function updateVariant() {
      var picked = [];
      info.querySelectorAll('input[type="radio"]:checked').forEach(function (i) { picked.push(i.value); });
      v.textContent = picked.join(' / ');
      var priceNow = info.querySelector('.price-item--regular, .price-item');
      if (priceNow) p.textContent = priceNow.textContent.trim();
      var soldOut = submit.getAttribute('disabled') !== null || submit.getAttribute('aria-disabled') === 'true';
      btn.classList.toggle('is-disabled', soldOut);
      btn.textContent = soldOut ? submit.textContent.trim() : (submit.textContent.trim() || 'Add to cart');
    }
    info.addEventListener('change', function () { setTimeout(updateVariant, 100); });
    updateVariant();

    var io = new IntersectionObserver(function (entries) {
      var show = !entries[0].isIntersecting && entries[0].boundingClientRect.top < 0;
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
    });
    io.observe(watch);
  }

  /* ------- Footer: collapsible blocks on phone (Hanger-style accordion) ------- */
  function initFooterAccordion(scope) {
    (scope || document).querySelectorAll('.footer .footer-block').forEach(function (block) {
      if (block.dataset.arkAcc) return;
      var heading = block.querySelector('.footer-block__heading');
      if (!heading || !heading.nextElementSibling) return;
      block.dataset.arkAcc = 'true';
      block.classList.add('hfoot-acc');
      heading.setAttribute('role', 'button');
      heading.setAttribute('tabindex', '0');
      heading.setAttribute('aria-expanded', 'false');
      function toggle() {
        /* collapsible on phones only — desktop keeps the plain open columns */
        if (!window.matchMedia('(max-width: 749px)').matches) return;
        var open = block.classList.toggle('is-open');
        heading.setAttribute('aria-expanded', open ? 'true' : 'false');
      }
      heading.addEventListener('click', toggle);
      heading.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); }
      });
    });
  }

  /* Run each initialiser in isolation so one throwing can NEVER stop the others
     (e.g. a gallery quirk must not prevent the fullscreen arrows/dots binding). */
  function safe(fn) {
    try { fn(); } catch (e) { if (window.console && console.warn) console.warn('[ark] init skipped:', e); }
  }

  function initAll(scope) {
    var root = scope || document;
    safe(function () { root.querySelectorAll('[data-hft]').forEach(initFeaturedTabs); });
    safe(function () { root.querySelectorAll('[data-hcc]').forEach(initCategoryCarousel); });
    safe(function () { root.querySelectorAll('[data-hvh]').forEach(initVideoHero); });
    safe(function () { root.querySelectorAll('.product__info-container').forEach(initPdpBuyRow); });
    safe(function () { initGalleryDots(root); });
    safe(function () { initLightboxDots(root); });
    safe(function () { initMediaSkeleton(root); });
    safe(function () { initStickyAtc(); });
    safe(function () { initFooterAccordion(root); });
    safe(function () { portalPopups(); });
  }

  /* Size-chart (and any product popup) rendered inside the product section can
     be trapped by transformed ancestors — position:fixed then anchors to the
     ancestor, not the viewport, and the popup shows sandwiched inside the page.
     Moving it to <body> makes the overlay bulletproof; Dawn's modal-opener
     looks the dialog up globally by id, so behaviour is unchanged. */
  function portalPopups() {
    document.querySelectorAll('modal-dialog.product-popup-modal, .product-popup-modal').forEach(function (m) {
      if (m.dataset.arkPortal || m.parentElement === document.body) return;
      m.dataset.arkPortal = 'true';
      document.body.appendChild(m);
    });
  }

  /* ZERO-TIMING GUARANTEE: also portal at the moment the opener is tapped, in
     the CAPTURE phase (runs before Dawn's own click handler shows the dialog).
     Covers dialogs injected at any time by any flow — page, quick view, apps. */
  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    var opener = e.target.closest('modal-opener[data-modal], [data-modal*="PopupModal"], a[href^="#PopupModal"]');
    if (!opener) return;
    var sel = opener.getAttribute('data-modal') || opener.getAttribute('href');
    if (!sel || sel.indexOf('PopupModal') === -1) return;
    var m = null;
    try { m = document.querySelector(sel); } catch (err) { m = null; }
    if (m && m.parentElement !== document.body) {
      m.dataset.arkPortal = 'true';
      document.body.appendChild(m);
    }
  }, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  /* Late-rendered content (product recommendations swap, predictive search,
     infinite content from apps): re-mark new media, debounced. */
  var moTimer = null;
  var mo = new MutationObserver(function () {
    if (moTimer) return;
    moTimer = setTimeout(function () {
      moTimer = null;
      safe(function () { initMediaSkeleton(document); });
      /* the fullscreen media modal can be (re)rendered late — re-bind its
         arrows + dots too (guarded, so it's a no-op if already bound). */
      safe(function () { initLightboxDots(document); });
      /* quick-view content is INJECTED after load and can carry a size-chart
         dialog — teleport it to <body> too, or the sheet's transform cages it */
      safe(function () { portalPopups(); });
    }, 200);
  });
  if (document.body) {
    mo.observe(document.body, { childList: true, subtree: true });
  } else {
    document.addEventListener('DOMContentLoaded', function () {
      mo.observe(document.body, { childList: true, subtree: true });
    });
  }

  /* Theme editor support */
  document.addEventListener('shopify:section:load', function (evt) {
    initAll(evt.target);
  });
})();
/* ============================================================================
   MEGA MENU — single consolidated controller (v40)
   - hover opens temporarily; click pins it open (click again / X / outside /
     Escape closes)
   - only ONE menu open at a time (opening any menu closes the others)
   - hovering a category swaps the right-hand image instantly; the first
     category is shown by default and restored when the cursor leaves the list
   Guarded so it binds each menu once, and re-binds after theme-editor reloads.
   ============================================================================ */
(function () {
  function closeMenuEl(m) {
    m.classList.remove('is-pinned', 'is-opening');
    m.removeAttribute('open');
  }
  function closeOthers(except) {
    document.querySelectorAll('details.mega-menu[open]').forEach(function (m) {
      if (m !== except) closeMenuEl(m);
    });
  }

  function initMega(root) {
    (root || document).querySelectorAll('details.mega-menu').forEach(function (menu) {
      if (menu.dataset.arkMega) return;
      menu.dataset.arkMega = '1';

      var leaveTimeout;
      var summary = menu.querySelector('summary');
      if (!summary) return;
      var closeBtn = menu.querySelector('.mega-menu__close');
      var imgs = menu.querySelectorAll('.mega-menu__featured-img');
      var area = menu.querySelector('.mega-menu__links-area');

      function showDefaultImage() {
        imgs.forEach(function (img, i) { img.classList.toggle('is-active', i === 0); });
      }

      // Hovering a category → activate the image whose key matches; default first.
      menu.querySelectorAll('.mega-menu__sublink[data-mega-img]').forEach(function (a) {
        a.addEventListener('mouseenter', function () {
          var key = a.getAttribute('data-mega-img');
          imgs.forEach(function (img) {
            img.classList.toggle('is-active', img.getAttribute('data-mega-key') === key);
          });
        });
      });
      if (area) area.addEventListener('mouseleave', showDefaultImage);

      function openMenu() {
        closeOthers(menu);           // enforce single-open
        menu.classList.remove('is-opening');
        void menu.offsetHeight;       // reflow so the reveal animation re-fires
        menu.setAttribute('open', '');
        menu.classList.add('is-opening');
        showDefaultImage();
      }

      // Hover: open temporarily.
      menu.addEventListener('mouseenter', function () {
        clearTimeout(leaveTimeout);
        if (!menu.hasAttribute('open')) openMenu();
      });
      // Leave: close after a short grace period — unless it was pinned by a click.
      menu.addEventListener('mouseleave', function () {
        if (menu.classList.contains('is-pinned')) return;
        leaveTimeout = setTimeout(function () { closeMenuEl(menu); }, 180);
      });
      // Click: toggle pinned (stays open until closed explicitly).
      summary.addEventListener('click', function (e) {
        e.preventDefault();
        if (menu.classList.contains('is-pinned')) {
          closeMenuEl(menu);
        } else {
          closeOthers(menu);
          if (!menu.hasAttribute('open')) openMenu();
          menu.classList.add('is-pinned');
        }
      });

      menu._close = function () { closeMenuEl(menu); };
      if (closeBtn) {
        closeBtn.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          closeMenuEl(menu);
          summary.focus();
        });
      }
    });
  }

  if (!window.__arkMegaDoc) {
    window.__arkMegaDoc = true;
    // Click anywhere outside an open menu closes it.
    document.addEventListener('click', function (e) {
      document.querySelectorAll('details.mega-menu[open]').forEach(function (menu) {
        if (!menu.contains(e.target)) closeMenuEl(menu);
      });
    });
    // Escape closes any open menu.
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      document.querySelectorAll('details.mega-menu[open]').forEach(function (menu) {
        var s = menu.querySelector('summary');
        closeMenuEl(menu);
        if (s) s.focus();
      });
    });
    document.addEventListener('shopify:section:load', function () { initMega(document); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initMega(document); });
  } else {
    initMega(document);
  }
})();

/* ============================================================================
   QUICK-VIEW GALLERY DOTS
   The quick-view modal content is injected via innerHTML, so inline <script>
   tags inside the section never run. A single delegated listener on document
   handles the dots for whatever quick-view is currently open. Clicking a dot
   activates the matching slide (number of dots === number of images).
   ============================================================================ */
(function () {
  if (window.__arkHqvDots) return;
  window.__arkHqvDots = true;

  function activate(mediaCol, idx) {
    mediaCol.querySelectorAll('[data-hqv-slide]').forEach(function (slide) {
      slide.classList.toggle('is-active', slide.getAttribute('data-hqv-slide') === idx);
    });
    mediaCol.querySelectorAll('[data-hqv-dot]').forEach(function (dot) {
      dot.classList.toggle('is-active', dot.getAttribute('data-hqv-dot') === idx);
    });
  }

  document.addEventListener('click', function (e) {
    var dot = e.target.closest ? e.target.closest('[data-hqv-dot]') : null;
    if (!dot) return;
    var mediaCol = dot.closest('.hqv__media-col');
    if (!mediaCol) return;
    e.preventDefault();
    activate(mediaCol, dot.getAttribute('data-hqv-dot'));
  });

  /* --- swipe left/right on the image to change slides (phones) --- */
  function currentIndex(mediaCol) {
    var active = mediaCol.querySelector('[data-hqv-slide].is-active');
    return active ? parseInt(active.getAttribute('data-hqv-slide'), 10) : 0;
  }
  function slideCount(mediaCol) {
    return mediaCol.querySelectorAll('[data-hqv-slide]').length;
  }
  var swipeCol = null, swipeX = 0, swipeY = 0;
  document.addEventListener('touchstart', function (e) {
    if (e.touches.length !== 1 || !e.target.closest) return;
    var col = e.target.closest('.hqv__media-col');
    if (!col || slideCount(col) < 2) { swipeCol = null; return; }
    swipeCol = col;
    swipeX = e.touches[0].clientX;
    swipeY = e.touches[0].clientY;
  }, { passive: true });
  document.addEventListener('touchend', function (e) {
    if (!swipeCol || !e.changedTouches || !e.changedTouches.length) return;
    var col = swipeCol; swipeCol = null;
    var dx = e.changedTouches[0].clientX - swipeX;
    var dy = e.changedTouches[0].clientY - swipeY;
    /* horizontal swipe only — vertical stays free for the sheet drag-to-close */
    if (Math.abs(dx) < 42 || Math.abs(dx) < Math.abs(dy) * 1.4) return;
    var n = slideCount(col);
    var next = currentIndex(col) + (dx < 0 ? 1 : -1);
    if (next < 0) next = 0;
    if (next > n - 1) next = n - 1;
    activate(col, String(next));
  }, { passive: true });
})();

/* ============================================================================
   QUICK-VIEW ADD-TO-CART GUARD
   The Add-to-cart button must reflect the SELECTED size's availability — not
   get stuck disabled. Dawn disables the button on every option change (loading
   state) and only re-enables it if its async re-render succeeds; if that fetch
   is unreliable inside the modal, the button stays disabled forever. This
   delegated guard resolves the selected variant from embedded data and sets the
   correct button/hidden-input state directly, so it never sticks. It runs after
   Dawn's synchronous disable (setTimeout 0) and agrees with Dawn on success.
   ============================================================================ */
(function () {
  if (window.__arkHqvAtc) return;
  window.__arkHqvAtc = true;

  function selectedValues(vs) {
    var out = [];
    vs.querySelectorAll('fieldset').forEach(function (fs) {
      var chk = fs.querySelector('input:checked');
      if (!chk) {
        var sel = fs.querySelector('select');
        if (sel && sel.selectedIndex > -1) chk = sel.options[sel.selectedIndex];
      }
      if (chk) out.push(chk.value);
    });
    return out;
  }

  function findVariant(variants, sel) {
    for (var i = 0; i < variants.length; i++) {
      var opts = variants[i].options || [];
      if (opts.length !== sel.length) continue;
      var ok = true;
      for (var j = 0; j < opts.length; j++) {
        if (String(opts[j]) !== String(sel[j])) { ok = false; break; }
      }
      if (ok) return variants[i];
    }
    return null;
  }

  function sync(root) {
    var dataEl = root.querySelector('[data-hqv-variants]');
    var vs = root.querySelector('variant-selects');
    if (!dataEl || !vs) return;
    var variants;
    try { variants = JSON.parse(dataEl.textContent); } catch (err) { return; }

    var sel = selectedValues(vs);
    if (!sel.length) return;
    var match = findVariant(variants, sel);

    var btn = root.querySelector('[id^="ProductSubmitButton-"]');
    var idInput = root.querySelector('form input[name="id"]') || root.querySelector('input.product-variant-id');

    if (idInput) {
      idInput.value = match ? match.id : '';
      if (match) idInput.removeAttribute('disabled');
      else idInput.setAttribute('disabled', 'disabled');
    }
    if (!btn) return;
    var span = btn.querySelector('span');
    if (match && match.available) {
      btn.removeAttribute('disabled');
      btn.removeAttribute('aria-disabled');
      btn.classList.remove('disabled');
      if (span) span.textContent = btn.getAttribute('data-atc-label') || 'Add to cart';
    } else {
      btn.setAttribute('disabled', 'disabled');
      if (span) span.textContent = match ? 'Sold out' : 'Unavailable';
    }
  }

  document.addEventListener('change', function (e) {
    if (!e.target.closest) return;
    var root = e.target.closest('[data-quick-view="true"]');
    if (!root) return;
    // Run after Dawn's synchronous button-disable so our state wins.
    setTimeout(function () { sync(root); }, 0);
  });
})();

/* ============================================================================
   MOBILE MENU — drag-to-close + tap-outside (phone only)
   The drawer (fixed sheet below the header) can be dragged down to dismiss, and
   tapping outside the sheet closes it. Closing is done by clicking the drawer's
   own <summary> so Dawn's close animation/cleanup runs normally.
   ============================================================================ */
(function () {
  if (window.__arkMenuDrag) return;
  window.__arkMenuDrag = true;

  function isPhone() { return window.matchMedia('(max-width: 749px)').matches; }
  function detailsEl() { return document.getElementById('Details-menu-drawer-container'); }
  function menuOpen() { var d = detailsEl(); return !!(d && d.hasAttribute('open')); }
  function closeMenu() {
    var s = document.querySelector('#Details-menu-drawer-container > summary');
    if (s) s.click();
  }

  /* Tap outside the sheet (while open) closes it. */
  document.addEventListener('click', function (e) {
    if (!isPhone() || !menuOpen() || !e.target.closest) return;
    if (e.target.closest('#menu-drawer')) return;                          // inside the sheet
    if (e.target.closest('#Details-menu-drawer-container > summary')) return; // the toggle itself
    closeMenu();
  });

  /* Drag the sheet down to dismiss — works with BOTH touch and mouse
     (so it also works in a desktop responsive/preview window). */
  var drawer = null, hd = null, scroller = null, startY = 0, dy = 0, dragging = false, mouse = false;

  function pointY(e) { return e.touches && e.touches[0] ? e.touches[0].clientY : e.clientY; }

  function startMenu(e, isMouse) {
    if (!isPhone() || !menuOpen() || drawer || !e.target.closest) return;
    var d = e.target.closest('#menu-drawer');
    if (!d) return;
    var sc = d.querySelector('.menu-drawer__navigation-container');
    if (sc && sc.scrollTop > 2) return; // let the menu scroll first
    drawer = d; scroller = sc; hd = d.closest('header-drawer'); mouse = isMouse;
    startY = pointY(e); dy = 0; dragging = false;
  }

  function moveMenu(e) {
    if (!drawer) return;
    var delta = pointY(e) - startY;
    if (delta <= 0) {
      if (dragging) { dragging = false; if (hd) hd.classList.remove('is-dragging'); drawer.style.removeProperty('--ark-drag'); }
      return;
    }
    if (scroller && scroller.scrollTop > 2) {
      if (dragging) { dragging = false; if (hd) hd.classList.remove('is-dragging'); drawer.style.removeProperty('--ark-drag'); }
      drawer = null; return;
    }
    dy = delta;
    if (!dragging && delta > 4) { dragging = true; if (hd) hd.classList.add('is-dragging'); }
    if (dragging) {
      /* The sheet's !important transform reads --ark-drag, so setting the
         variable inline is what actually moves it (style.transform cannot). */
      drawer.style.setProperty('--ark-drag', dy + 'px');
      if (!mouse && e.cancelable) e.preventDefault();
    }
  }

  function endMenu() {
    if (!drawer) return;
    var d = drawer, h = hd, moved = dy, was = dragging;
    if (h) h.classList.remove('is-dragging');   /* easing transition comes back */
    drawer = null; scroller = null; hd = null; dy = 0; dragging = false;
    if (was && moved > 80) {
      /* Fling the sheet the rest of the way down, then let Dawn close for real. */
      d.style.setProperty('--ark-drag', '110%');
      setTimeout(function () {
        closeMenu();
        setTimeout(function () { d.style.removeProperty('--ark-drag'); }, 80);
      }, 260);
    } else {
      d.style.removeProperty('--ark-drag');     /* springs back to translateY(0) */
    }
  }

  document.addEventListener('touchstart', function (e) { if (e.touches.length === 1) startMenu(e, false); }, { passive: true });
  document.addEventListener('touchmove', moveMenu, { passive: false });
  document.addEventListener('touchend', endMenu);
  document.addEventListener('touchcancel', endMenu);
  document.addEventListener('mousedown', function (e) { if (e.button === 0) startMenu(e, true); }, { passive: true });
  document.addEventListener('mousemove', moveMenu, { passive: true });
  document.addEventListener('mouseup', endMenu);

  /* Back-forward cache restores the page WITH the drawer's tapped state (open
     submenus, focused link) — reset it so nothing looks "selected" on return. */
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    try {
      var d = detailsEl();
      if (d) { d.classList.remove('menu-opening'); d.removeAttribute('open'); }
      document.querySelectorAll('#menu-drawer details[open]').forEach(function (dd) {
        dd.classList.remove('menu-opening');
        dd.removeAttribute('open');
      });
      document.querySelectorAll('#menu-drawer .submenu-open').forEach(function (el) {
        el.classList.remove('submenu-open');
      });
      document.body.classList.remove('overflow-hidden-mobile', 'overflow-hidden-tablet', 'overflow-hidden');
      if (document.activeElement && document.activeElement.blur) document.activeElement.blur();
    } catch (err) { /* never break the page over cleanup */ }
  });
})();

/* ============================================================================
   QUICK VIEW — drag-to-close (phone only)
   Drag the bottom sheet down past a threshold to dismiss it.
   ============================================================================ */
(function () {
  if (window.__arkQvDrag) return;
  window.__arkQvDrag = true;

  function isPhone() { return window.matchMedia('(max-width: 749px)').matches; }
  var sheet = null, modal = null, scroller = null, startY = 0, dy = 0, dragging = false;

  document.addEventListener('touchstart', function (e) {
    if (!isPhone() || e.touches.length !== 1 || !e.target.closest) return;
    var m = e.target.closest('quick-add-modal[open]');
    if (!m) return;
    var c = m.querySelector('.quick-add-modal__content');
    if (!c || !c.contains(e.target)) return;
    if (c.scrollTop > 2) return;
    sheet = c; modal = m; scroller = c;
    startY = e.touches[0].clientY; dy = 0; dragging = false;
  }, { passive: true });

  document.addEventListener('touchmove', function (e) {
    if (!sheet) return;
    var delta = e.touches[0].clientY - startY;
    if (delta <= 0) {
      if (dragging) { dragging = false; modal.classList.remove('is-dragging'); sheet.style.transform = ''; }
      return;
    }
    if (scroller && scroller.scrollTop > 2) {
      if (dragging) { dragging = false; modal.classList.remove('is-dragging'); sheet.style.transform = ''; }
      sheet = null; return;
    }
    dy = delta; dragging = true;
    modal.classList.add('is-dragging');
    sheet.style.transform = 'translateY(' + dy + 'px)';
    if (e.cancelable) e.preventDefault();
  }, { passive: false });

  function endQv() {
    if (!sheet) return;
    var s = sheet, m = modal, moved = dy, was = dragging;
    m.classList.remove('is-dragging');
    sheet = null; modal = null; scroller = null; dy = 0; dragging = false;
    if (was && moved > 90) {
      s.style.transition = 'transform 0.28s ease';
      s.style.transform = 'translateY(100%)';
      setTimeout(function () {
        if (typeof m.hide === 'function') m.hide();
        else { var t = m.querySelector('.quick-add-modal__toggle'); if (t) t.click(); }
        s.style.transition = ''; s.style.transform = '';
      }, 240);
    } else {
      s.style.transform = '';
    }
  }
  document.addEventListener('touchend', endQv);
  document.addEventListener('touchcancel', endQv);
})();